<template>
    <div class="lesson-chat-history-viewer">
        <b-modal
            :id="modalId"
            :title="modalTitle"
            size="lg"
            scrollable
            hide-footer
            @hidden="onModalHidden"
        >
            <div v-if="loading" class="text-center py-4">
                <b-spinner variant="primary" label="Loading"></b-spinner>
            </div>
            <div v-else-if="loadError" class="alert alert-warning mb-0">
                {{ loadError }}
            </div>
            <div v-else-if="messages.length === 0" class="text-center text-muted py-4 small">
                No lesson chat messages for this lesson.
            </div>
            <div v-else class="lesson-chat-history-list">
                <div
                    v-for="msg in messages"
                    :key="'lch-' + msg.id"
                    class="lesson-chat-history-item"
                    :class="{ 'lesson-chat-history-item--mine': isMine(msg) }"
                >
                    <div class="lesson-chat-history-meta small text-muted">
                        <strong>{{ senderLabel(msg) }}</strong>
                        <span v-if="formatTime(msg)"> · {{ formatTime(msg) }}</span>
                    </div>
                    <div class="lesson-chat-history-body chat-message-body" v-html="formatMessage(msg.message)"></div>
                </div>
            </div>
            <div class="text-right mt-3">
                <b-button size="sm" variant="secondary" @click="close">Close</b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import * as Moment from 'moment';

export default {
    name: 'LessonChatHistoryViewerComponent',
    props: {
        api_token: {
            type: String,
            required: true,
        },
        modal_id: {
            type: String,
            default: 'lesson-chat-history-modal',
        },
    },
    data() {
        return {
            scheduleId: null,
            modalTitle: 'Lesson chat',
            loading: false,
            loadError: null,
            messages: [],
            currentUserId: null,
        };
    },
    computed: {
        modalId() {
            return this.modal_id;
        },
    },
    mounted() {
        if (typeof window !== 'undefined' && window.__lessonChatViewerUserId) {
            this.currentUserId = String(window.__lessonChatViewerUserId);
        }
        document.addEventListener('click', this.onDocumentClick);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick);
    },
    methods: {
        onDocumentClick(event) {
            const trigger = event.target.closest('.js-view-lesson-chat');
            if (!trigger) {
                return;
            }
            event.preventDefault();
            const scheduleId = trigger.getAttribute('data-schedule-id') || trigger.dataset.scheduleId;
            const label = trigger.getAttribute('data-lesson-label') || trigger.dataset.lessonLabel || '';
            if (scheduleId) {
                this.open(scheduleId, label);
            }
        },
        open(scheduleId, title) {
            this.scheduleId = String(scheduleId);
            this.modalTitle = title ? `Lesson chat — ${title}` : 'Lesson chat';
            this.loadError = null;
            this.messages = [];
            this.$bvModal.show(this.modalId);
            this.fetchHistory();
        },
        close() {
            this.$bvModal.hide(this.modalId);
        },
        onModalHidden() {
            this.loading = false;
            this.loadError = null;
            this.messages = [];
        },
        isMine(msg) {
            if (!this.currentUserId || !msg || msg.sender_id == null) {
                return false;
            }
            return String(msg.sender_id) === this.currentUserId;
        },
        senderLabel(msg) {
            if (msg.nickname) {
                return msg.nickname;
            }
            if (msg.firstname) {
                return msg.firstname;
            }
            if (msg.message_type === 'TUTOR') {
                return 'Tutor';
            }
            return 'Student';
        },
        formatTime(msg) {
            const raw = msg.sent_at || msg.created_at;
            if (!raw) {
                return '';
            }
            const m = Moment(raw);
            return m.isValid() ? m.format('YYYY-MM-DD HH:mm') : String(raw);
        },
        formatMessage(html) {
            if (html == null || html === '') {
                return '';
            }
            return String(html);
        },
        fetchHistory() {
            if (!this.scheduleId || !this.api_token) {
                this.loadError = 'Missing lesson or login token.';
                return;
            }
            this.loading = true;
            axios.post('/api/getLessonChathistory?api_token=' + encodeURIComponent(this.api_token), {
                method: 'POST',
                channelid: this.scheduleId,
            }).then((response) => {
                this.loading = false;
                if (response.data && response.data.success === true) {
                    const list = response.data.allChatHistoryItems || [];
                    this.messages = Array.isArray(list) ? list : [];
                    return;
                }
                if (response.data && response.data.success === false) {
                    this.messages = [];
                    return;
                }
                this.loadError = 'Could not load lesson chat.';
            }).catch((error) => {
                this.loading = false;
                const status = error.response && error.response.status;
                if (status === 403) {
                    this.loadError = 'You do not have permission to view this lesson chat.';
                } else {
                    this.loadError = 'Could not load lesson chat.';
                }
                console.warn('[lesson-chat-history]', error);
            });
        },
    },
};
</script>

<style scoped>
.lesson-chat-history-list {
    max-height: 60vh;
    overflow-y: auto;
    padding: 0.25rem;
}

.lesson-chat-history-item {
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.65rem;
    border-radius: 8px;
    background: #f1f5f9;
}

.lesson-chat-history-item--mine {
    background: #e0f2fe;
}

.lesson-chat-history-body >>> img {
    max-width: 100%;
    height: auto;
}
</style>
